// 100%コンプ（やりこみ達成）の進捗計算。v0.8.0 のバランス改修で「ゴール」を明示する。
//
// このゲームは cozy 放置で失敗が無いが、これまで「どこまでやれば一区切りか」の目印が
// 無かった。4 つの軸（図鑑・島・ドリンク・強化）の達成率を1か所で計算し、HUD/ステータスに
// 「コンプ率」として見せる。バランス検証（scripts/balance）はこの定義と完全に一致させてある:
// シミュレーションした最速攻略は約 20 時間（古木が育ち樹守ねむを呼ぶ瞬間が capstone）。
//
// 描画・DOM・乱数に触れない純関数。状態 GameState からの導出だけ。

import type { GameState } from './state';
import { MAX_ISLANDS } from './state';
import { CUSTOMER_CATALOG } from './customers';
import { DRINK_CATALOG } from './menu';
import { MAX_LEVEL } from './decos';
import { cellLevel } from './garden';

/**
 * 強化（mastery）軸の達成に必要な「Lv5 タイル数」。バランス検証で、全 6 戦略が
 * 約 20 時間で 100% に到達し、かつどれか 1 戦略が突出しない（同格）ことを確認した弾力ノブ。
 * これは「庭をしっかり育てきった」を表す中盤〜後半の目標（島総アンロックより少し手前で埋まる）。
 */
export const MASTERY_TARGET = 320;

/** コンプ 1 軸の進捗。 */
export interface CompletionAxis {
  /** 安定キー。 */
  key: 'dex' | 'islands' | 'drinks' | 'mastery';
  /** 表示名。 */
  label: string;
  /** アイコン（絵文字）。 */
  icon: string;
  /** 現在値。 */
  value: number;
  /** 目標値。 */
  total: number;
  /** 達成したか。 */
  done: boolean;
}

/** 100%コンプ全体の進捗。 */
export interface CompletionProgress {
  axes: CompletionAxis[];
  /** 0..1。4 軸の達成率の平均（各軸は value/total を 1 で頭打ち）。 */
  fraction: number;
  /** 0..100 の整数（表示用）。 */
  percent: number;
  /** 全軸達成（真の 100%）。 */
  done: boolean;
}

/** 全アンロック島で Lv5 に達したタイル数（強化軸の現在値）。 */
export function masteryTileCount(state: GameState): number {
  let count = 0;
  const n = Math.min(state.unlockedIslands, state.islands.length);
  for (let i = 0; i < n; i++) {
    const grid = state.islands[i].garden;
    for (const cell of grid) {
      if (cell && cellLevel(cell) >= MAX_LEVEL) count++;
    }
  }
  return count;
}

/** 図鑑で発見済みの客種数。 */
export function discoveredCount(state: GameState): number {
  let dex = 0;
  for (const c of CUSTOMER_CATALOG) if (state.dex[c.id]?.discovered) dex++;
  return dex;
}

/**
 * 100%コンプの進捗を計算する純関数。4 軸（図鑑・島・ドリンク・強化）。
 */
export function completionProgress(state: GameState): CompletionProgress {
  const dex = discoveredCount(state);
  const dexTotal = CUSTOMER_CATALOG.length;
  const islands = state.unlockedIslands;
  const drinks = state.menu.developed.length;
  const drinksTotal = DRINK_CATALOG.length;
  const mastery = masteryTileCount(state);

  const axes: CompletionAxis[] = [
    { key: 'dex', label: '図鑑', icon: '📖', value: dex, total: dexTotal, done: dex >= dexTotal },
    { key: 'islands', label: '島', icon: '🏝️', value: islands, total: MAX_ISLANDS, done: islands >= MAX_ISLANDS },
    { key: 'drinks', label: 'ドリンク', icon: '☕', value: drinks, total: drinksTotal, done: drinks >= drinksTotal },
    { key: 'mastery', label: '強化', icon: '✦', value: mastery, total: MASTERY_TARGET, done: mastery >= MASTERY_TARGET },
  ];

  let sum = 0;
  for (const a of axes) sum += Math.min(1, a.total > 0 ? a.value / a.total : 1);
  const fraction = sum / axes.length;
  return {
    axes,
    fraction,
    percent: Math.floor(fraction * 100),
    done: axes.every((a) => a.done),
  };
}
